import { DEFAULT_OTP_RETRY_SECONDS, MfaApiError } from '../../api/mfa'

/** "30 seconds", "1 minute", "2 minutes" — the server's wait, rounded up to whole minutes from 60s. */
function waitPhrase(s: number): string {
  if (s < 60) return s === 1 ? '1 second' : `${s} seconds`
  const m = Math.ceil(s / 60)
  return m === 1 ? '1 minute' : `${m} minutes`
}

/**
 * The words for a failed backend-MFA call — ONE source for the step-up sheet,
 * the recovery-codes sheet and the email-codes sheet, so the same failure never
 * reads three ways.
 *
 * **A wrong code and an expired one share a message on purpose.** The backend
 * answers both with the same 400 ("Verification failed." / "Invalid or expired
 * OTP code."), so the client cannot tell them apart and must not pretend to.
 * The one expiry it CAN tell — the whole challenge timing out — is the
 * `expired` kind, which `StepUpSheet` handles as its own phase.
 */
export function mfaMessage(
  thrown: unknown,
  /** Per-call wording: for a server that can't be reached (default: the server's own), and for anything unclassified. */
  wording: { unavailable?: string; fallback?: string } = {},
): string {
  if (thrown instanceof MfaApiError) {
    switch (thrown.kind) {
      case 'invalid_code':
        return 'That code is wrong or has expired. Check it, or ask for a new one.'
      case 'step_up_locked':
        return 'Too many wrong codes. Try again in 15 minutes.'
      case 'otp_send_limited':
        return `Too many codes sent. Wait ${waitPhrase(thrown.retryAfterSeconds ?? DEFAULT_OTP_RETRY_SECONDS)}, then try again.`
      case 'rate_limited':
        return 'Too many attempts. Wait a few minutes and try again.'
      case 'step_up_required':
      case 'step_up_invalid':
        return 'That confirmation didn’t go through. Try again.'
      case 'unavailable':
        return wording.unavailable ?? thrown.message
      default:
        break
    }
  }
  return wording.fallback ?? 'Something didn’t work. Try again.'
}

/** Shown when the user backs out of a re-auth or step-up that a change needed. */
export const CANCELLED_MESSAGE = 'You cancelled before confirming it’s you. Nothing was changed.'
